import {
  type BlockKind,
  type BlockStatus,
  type Extraction,
  type ExtractedBlock,
  type Region,
} from '@/platform/materials/extraction'

/**
 * Learner review of a real extraction (Slice 3): every block that is not
 * clearly read must be checked against the original and either accepted as
 * read, corrected or left out before anything can be confirmed. Clear blocks
 * start accepted but can still be corrected or left out.
 *
 * The confirmed record keeps the chain
 *
 *   Original document → Extracted block → Confirmed text
 *
 * for every block: the document's hash, page and region, what was read and
 * how sure the reading was, and what the learner confirmed. Confirmation
 * means "this matches my page", not that the content is academically correct.
 */

export type Resolution =
  | { readonly kind: 'pending' }
  | { readonly kind: 'accepted' }
  | { readonly kind: 'corrected'; readonly text: string }
  | { readonly kind: 'excluded' }

export interface ReviewState {
  readonly extraction: Extraction
  readonly resolutions: ReadonlyMap<string, Resolution>
}

export function startReview(extraction: Extraction): ReviewState {
  return {
    extraction,
    resolutions: new Map(
      extraction.blocks.map((b) => [
        b.id,
        b.status === 'clear' ? { kind: 'accepted' } : { kind: 'pending' },
      ]),
    ),
  }
}

export function resolutionOf(state: ReviewState, blockId: string): Resolution {
  return state.resolutions.get(blockId) ?? { kind: 'pending' }
}

/** Whether `resolution` is allowed for this block. Nothing unreadable is accepted as read. */
export function canResolve(block: ExtractedBlock, resolution: Resolution): boolean {
  switch (resolution.kind) {
    case 'pending':
      return block.status !== 'clear'
    case 'accepted':
      return block.status !== 'unreadable' && block.text.trim() !== ''
    case 'corrected':
      return resolution.text.trim() !== ''
    case 'excluded':
      return true
  }
}

export function resolve(
  state: ReviewState,
  blockId: string,
  resolution: Resolution,
): ReviewState {
  const block = state.extraction.blocks.find((b) => b.id === blockId)
  if (block === undefined || !canResolve(block, resolution)) return state
  // Correcting to exactly what was read is accepting it as read.
  const next: Resolution =
    resolution.kind === 'corrected' && resolution.text === block.text
      ? { kind: 'accepted' }
      : resolution
  return { ...state, resolutions: new Map(state.resolutions).set(blockId, next) }
}

export function pendingBlocks(state: ReviewState): readonly ExtractedBlock[] {
  return state.extraction.blocks.filter(
    (b) => resolutionOf(state, b.id).kind === 'pending',
  )
}

export function includedBlocks(state: ReviewState): readonly ExtractedBlock[] {
  return state.extraction.blocks.filter((b) => {
    const kind = resolutionOf(state, b.id).kind
    return kind === 'accepted' || kind === 'corrected'
  })
}

export function canConfirm(state: ReviewState): boolean {
  return pendingBlocks(state).length === 0 && includedBlocks(state).length > 0
}

export interface ConfirmedBlock {
  readonly blockId: string
  readonly page: number
  readonly order: number
  readonly region: Region | null
  readonly kind: BlockKind
  /** What the provider read, unchanged. */
  readonly extractedText: string
  /** How sure the reading was before the learner checked it. */
  readonly extractedStatus: BlockStatus
  readonly resolution: 'accepted' | 'corrected'
  /** The text the learner confirmed against the original. */
  readonly text: string
}

export interface ConfirmedMaterial {
  readonly subject: Extraction['subject']
  readonly source: {
    readonly fileName: string
    readonly mimeType: string
    readonly bytes: number
    readonly documentSha256: string
    readonly pageCount: number
  }
  readonly extraction: {
    readonly id: string
    readonly provider: Extraction['provider']
    readonly extractedAt: string
  }
  readonly confirmedAt: string
  readonly blocks: readonly ConfirmedBlock[]
  /** Left out by the learner: kept as references, never used as content. */
  readonly excluded: readonly {
    readonly blockId: string
    readonly page: number
    readonly extractedStatus: BlockStatus
  }[]
}

/** The confirmed record, or null while anything is still pending. */
export function confirm(
  state: ReviewState,
  fileName: string,
  now: Date,
): ConfirmedMaterial | null {
  if (!canConfirm(state)) return null
  const e = state.extraction
  const blocks: ConfirmedBlock[] = []
  const excluded: ConfirmedMaterial['excluded'][number][] = []
  for (const b of e.blocks) {
    const r = resolutionOf(state, b.id)
    if (r.kind === 'excluded') {
      excluded.push({ blockId: b.id, page: b.page, extractedStatus: b.status })
      continue
    }
    if (r.kind === 'pending') return null
    blocks.push({
      blockId: b.id,
      page: b.page,
      order: b.order,
      region: b.region,
      kind: b.kind,
      extractedText: b.text,
      extractedStatus: b.status,
      resolution: r.kind,
      text: r.kind === 'corrected' ? r.text.trim() : b.text,
    })
  }
  return {
    subject: e.subject,
    source: {
      fileName,
      mimeType: e.mimeType,
      bytes: e.bytes,
      documentSha256: e.documentSha256,
      pageCount: e.pageCount,
    },
    extraction: { id: e.id, provider: e.provider, extractedAt: e.extractedAt },
    confirmedAt: now.toISOString(),
    blocks,
    excluded,
  }
}
